export const PLAYTECH_API_EVENTS = {
    /**
     * Event fires when two factor authentication popup was closed.
     * @memberOf Playtech.API.events
     * @event TWO_FA_POPUP_CLOSED
     * @property {String[]} Component.
     * @example Playtech.on('TWO_FA_POPUP_CLOSED', ([Component]) => {
     *     // ...
     * });
     */
    TWO_FA_POPUP_CLOSED: '2fa-popup-closed',
    // This event is described in the file useOnCookieSettingsUpdated.js
    COOKIE_REGULATION: 'cookie-settings-updated',
    /**
     * Event fires when language in Portal is changed.
     * @memberOf Playtech.API.events
     * @event LANGUAGE_CHANGED
     * @property {String[]} The value of the new language.
     * @example Playtech.on('LANGUAGE_CHANGED', ([lang]) => {
     *     // ...
     * });
     */
    LANGUAGE_CHANGED: 'user:action:changeLanguage',
    /**
     * Event fires when user successfully login on the portal.
     * @memberOf Playtech.API.events
     * @event LOGGED_IN
     * @example Playtech.on('LOGGED_IN', () => {
     *     // ...
     * });
     */
    LOGGED_IN: 'auth:credentials:login',
    /**
     * The event fires when the user fails to login.
     * @memberOf Playtech.API.events
     * @event LOGGED_IN_FAIL
     * @property {String[]} Login error.
     * @example Playtech.on('LOGGED_IN_FAIL', ([error]) => {
     *     // ...
     * });
     */
    LOGGED_IN_FAIL: 'auth:credentials:login:failed',
    /**
     * The event fires when the user logout on the portal.
     * @memberOf Playtech.API.events
     * @event LOGGED_OUT
     * @example Playtech.on('LOGGED_OUT', () => {
     *     // ...
     * });
     */
    LOGGED_OUT: 'auth:logout',
    /**
     * Event fires on successful registration.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_COMPLETED
     * @property {Array.<{ username: String, userId: String, playerCode: Number }>} options
     * @example Playtech.on('REGISTRATION_COMPLETED', ([{ username, userId, playerCode }]) => {
     *     // ...
     * });
     */
    REGISTRATION_COMPLETED: 'registration:success',
    /**
     * The event fires when an error occurs during registration.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_FAIL
     * @property {Array.<{ content: String }>} options
     * @example Playtech.on('REGISTRATION_FAIL', ([{ content }]) => {
     *     // ...
     * });
     */
    REGISTRATION_FAIL: 'registration:error',
    /**
     * The event fires when the registration step changes.
     * @memberOf Playtech.API.events
     * @event REGISTRATION_STEP_CHANGED
     * @property {Array.<String|Number>} options
     * @example Playtech.on('REGISTRATION_STEP_CHANGED', ([nextStep, currentStep]) => {
     *     // ...
     * });
     */
    REGISTRATION_STEP_CHANGED: 'registration:changeStep',
    /**
     * The event fires when the deposit has been successfully completed.
     * @memberOf Playtech.API.events
     * @event DEPOSIT_COMPLETED
     * @property {Array.<{ amount: Number, code: String, method: String, status: String }>} options
     * @example Playtech.on('DEPOSIT_COMPLETED', ([{ amount: 1, code: '40564463', method: 'Retail Deposit', status: 'waiting' }]) => {
     *     // ...
     * });
     */
    DEPOSIT_COMPLETED: 'deposit:success',
    /**
     * The event is fired when the deposit was not completed successfully.
     * @memberOf Playtech.API.events
     * @event DEPOSIT_FAIL
     * @property {Array.<{ correlationId: String, error: Object, errorCode: Number, errorDescription: String, errorMessage: String }>} options
     * @example Playtech.on('DEPOSIT_FAIL', ([{ correlationId, error, errorCode, errorDescription, errorMessage }]) => {
     *     // ...
     * });
     */
    DEPOSIT_FAIL: 'deposit:fail',
    /**
     * The event fires when the withdraw has been successfully completed.
     * @memberOf Playtech.API.events
     * @event WITHDRAWAL_COMPLETED
     * @property {Array.<{ amount, method, status, transactionCode }>} options
     * @example Playtech.on('WITHDRAWAL_COMPLETED', ([{ amount, method, status, transactionCode }]) => {
     *     // ...
     * });
     */
    WITHDRAWAL_COMPLETED: 'withdraw:success',
    /**
     * The event is fired when the withdraw was not completed successfully.
     * @memberOf Playtech.API.events
     * @event WITHDRAWAL_FAIL
     * @property {Array.<{ correlationId: String, error: Object, errorCode: Number, errorDescription: String, errorMessage: String }>} options
     * @example Playtech.on('WITHDRAWAL_FAIL', ([{ correlationId, error, errorCode, errorDescription, errorMessage }]) => {
     *     // ...
     * });
     */
    WITHDRAWAL_FAIL: 'withdraw:fail',
    /**
     * The event is fired when the request forgot username is successful.
     * @memberOf Playtech.API.events
     * @event FORGOT_USERNAME_SUCCESS
     * @example Playtech.on('FORGOT_USERNAME_SUCCESS', () => {
     *     // ...
     * });
     */
    FORGOT_USERNAME_SUCCESS: 'forgotUsername:success',
    /**
     * The event is fired when the request forgot username is failed.
     * @memberOf Playtech.API.events
     * @event FORGOT_USERNAME_FAIL
     * @example Playtech.on('FORGOT_USERNAME_FAIL', () => {
     *     // ...
     * });
     */
    FORGOT_USERNAME_FAIL: 'forgotUsername:fail',
    /**
     * The event is fired when the request forgot password is successful.
     * @memberOf Playtech.API.events
     * @event FORGOT_PASSWORD_SUCCESS
     * @example Playtech.on('FORGOT_PASSWORD_SUCCESS', () => {
     *     // ...
     * });
     */
    FORGOT_PASSWORD_SUCCESS: 'forgotPassword:success',
    /**
     * The event is fired when the request forgot password is failed.
     * @memberOf Playtech.API.events
     * @event FORGOT_PASSWORD_FAIL
     * @property {Array.<{ data: { errorCode: Number, errorText: String, playerMessage: String, requestId: String, sessionRef: String } }>} options
     * @example Playtech.on('FORGOT_PASSWORD_FAIL', ([{ data: { errorCode, errorText, playerMessage, requestId, sessionRef } }]) => {
     *     // ...
     * });
     */
    FORGOT_PASSWORD_FAIL: 'forgotPassword:fail',
    /**
     * The event is fired after action "analytics" is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_ANALYTICS
     * @example Playtech.on('SAFECHARGE_ANALYTICS', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_ANALYTICS: 'safecharge:analytics',
    /**
     * The event is fired after payment method registration failed action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_PM_REGISTRATION_FAIL
     * @example Playtech.on('SAFECHARGE_PM_REGISTRATION_FAIL', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_PM_REGISTRATION_FAIL: 'safecharge:failedRegistration',
    /**
     * The event is fired after payment method successful registration action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_PM_REGISTRATION_SUCCESS
     * @example Playtech.on('SAFECHARGE_PM_REGISTRATION_SUCCESS', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_PM_REGISTRATION_SUCCESS: 'safecharge:successfulRegistration',
    /**
     * The event is fired after contact support action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_CONTACT_SUPPORT
     * @example Playtech.on('SAFECHARGE_CONTACT_SUPPORT', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_CONTACT_SUPPORT: 'safecharge:contactSupport',
    /**
     * The event is fired after alternative verification action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_ALTERNATIVE_VERIFICATION
     * @example Playtech.on('SAFECHARGE_ALTERNATIVE_VERIFICATION', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_ALTERNATIVE_VERIFICATION: 'safecharge:alternativeVerification',
    /**
     * The event is fired after successful withdraw action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_WITHDRAWAL_SUCCESS
     * @example Playtech.on('SAFECHARGE_WITHDRAWAL_SUCCESS', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_WITHDRAWAL_SUCCESS: 'safecharge:successfulWithdrawalRequest',
    /**
     * The event is fired after withdraw failed action is received with payload as an argument.
     * @memberOf Playtech.API.events
     * @event SAFECHARGE_WITHDRAWAL_FAIL
     * @example Playtech.on('SAFECHARGE_WITHDRAWAL_FAIL', (data) => {
     *     // ...
     * });
     */
    SAFECHARGE_WITHDRAWAL_FAIL: 'safecharge:failedWithdrawalRequest',
    /**
     * The event is fired when the web content was rendered.
     * @memberOf Playtech.API.events
     * @event WEB_CONTENT_RENDERED
     * @property {Array.<{ wc: String }>} options
     * @example Playtech.on('WEB_CONTENT_RENDERED', ([wc]) => {
     *     // ...
     * });
     */
    WEB_CONTENT_RENDERED: 'webContent:rendered',
    /**
     * The event is fired when games portlet is mounted.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_DISPLAY
     * @property {Array.<{ container: String, gameFeed: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_DISPLAY', ([{ container, gameFeed }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_DISPLAY: 'games:mount',
    /**
     * The event is fired when game portlet category was switched.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH
     * @property {Array.<{ container: String, gameFeed: String, categoryName: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH', ([{ container, gameFeed, categoryName }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_SWITCH: 'game:category:switch',
    /**
     * The event is fired when games category is expanded.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND
     * @property {Array.<{ container: String, gameFeed: String, categoryName: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND', ([{ container, gameFeed, categoryName }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_EXPAND: 'game:category:expand',
    /**
     * The event is fired when active slide was changed.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL
     * @property {Array.<{ container: String, gameFeed: String, categoryName: String, elementClicked: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL', ([{ container, gameFeed, categoryName, elementClicked }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_CATEGORY_SCROLL: 'game:category:activeSlide:change',
    /**
     * The event is fired when personalized game grid category is configured.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2
     * @property {Array.<{ container: String, gameFeed: String, personalizedCategoryName: String, personalizedGridFallback: Number, pagePath: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2', ([{ container, gameFeed, personalizedCategoryName, personalizedGridFallback, pagePath }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_PERSONALIZED_GRID_2: 'games:personalizedGrid:2',
    /**
     * The event is fired when search field input is changed.
     * @memberOf Playtech.API.events
     * @event RESPONSIVE_GAME_PORTLET_SEARCH
     * @property {Array.<{ gameFeed: String, searchString: string, container: String }>} options
     * @example Playtech.on('RESPONSIVE_GAME_PORTLET_SEARCH', ([{ gameFeed, searchString, container }]) => {
     *     // ...
     * });
     */
    RESPONSIVE_GAME_PORTLET_SEARCH: 'game:search:change',
    /**
     * The event is fired when a game is added/removed from the favorites.
     * @memberOf Playtech.API.events
     * @event GAME_FAVORITES_HANDLER
     * @property {Array.<{ gameCode: String, method: String, gameFeed: String, container: String }>} options
     * @example Playtech.on('GAME_FAVORITES_HANDLER', ([{ gameFeed, method, gameCode, container }]) => {
     *     // ...
     * });
     */
    GAME_FAVORITES_HANDLER: 'gameFavoritesChange',
    /**
     * The event is fired when a game is launched.
     * @memberOf Playtech.API.events
     * @event GAME_LAUNCH
     * @property {Array.<{ launchType: String, gameCode: String, gameName: String, gameFeed: String, container: String }>} options
     * @example Playtech.on('GAME_LAUNCH', ([{ launchType, gameCode, gameName, gameFeed, container }]) => {
     *     // ...
     * });
     */
    GAME_LAUNCH: 'gameGameLaunch'
};
